/**
 * Aggregate Zendesk View Component
 *
 * Displays support ticket analytics across all brands the user has access to.
 * Features per-brand breakdown, stacked charts, BrandFilter, and WoW trends.
 */

import { format } from 'date-fns'
import { useMemo, useState, useRef, useEffect } from 'react'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line,
  ComposedChart, Area, AreaChart,
} from 'recharts'
import { Card, CardContent, CardDescription, CardHeader, CardTitle, Button, Badge } from './ui'
import { getBrandColor } from '../lib/brandColors'
import { zendeskAPI } from '../services/api'
import {
  Loader2, Filter, Check, ChevronDown, AlertTriangle,
} from 'lucide-react'

// Format numbers
function formatNumber(value) {
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`
  if (value >= 1000) return `${(value / 1000).toFixed(1)}K`
  return Math.round(value).toLocaleString()
}

function formatMinutes(mins) {
  if (mins == null) return '--'
  if (mins < 60) return `${Math.round(mins)}m`
  if (mins < 1440) return `${(mins / 60).toFixed(1)}h`
  return `${(mins / 1440).toFixed(1)}d`
}

function formatHours(hrs) {
  if (hrs == null) return '--'
  if (hrs < 1) return `${Math.round(hrs * 60)}m`
  if (hrs < 24) return `${hrs.toFixed(1)}h`
  return `${(hrs / 24).toFixed(1)}d`
}

function formatCSAT(score) {
  if (score == null) return '--'
  return `${(score * 100).toFixed(0)}%`
}

// Pie chart label
function renderPieLabel({ cx, cy, midAngle, innerRadius, outerRadius, percent }) {
  // Recharts passes `percent` as a 0-1 fraction, but it also spreads the datum
  // into these props — so a data key named `percent` silently replaces it with
  // whatever the data holds. Anything above 1 cannot be a fraction, so it is
  // already a percentage and must not be multiplied again.
  const fraction = percent > 1 ? percent / 100 : percent
  if (!(fraction >= 0.05)) return null
  const RADIAN = Math.PI / 180
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5
  const x = cx + radius * Math.cos(-midAngle * RADIAN)
  const y = cy + radius * Math.sin(-midAngle * RADIAN)
  return (
    <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central"
      className="text-xs font-bold" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
      {`${(fraction * 100).toFixed(0)}%`}
    </text>
  )
}

function ChartTooltip({ active, payload, label, valueFormatter }) {
  if (active && payload && payload.length) {
    return (
      <div className="bg-popover border border-border rounded-lg shadow-lg p-3 text-sm">
        <p className="font-medium text-popover-foreground mb-2">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ color: entry.color || entry.stroke }} className="flex justify-between gap-4">
            <span>{entry.name}:</span>
            <span className="font-medium">
              {valueFormatter ? valueFormatter(entry.value, entry.name) : entry.value}
            </span>
          </p>
        ))}
      </div>
    )
  }
  return null
}

function PieTooltip({ active, payload }) {
  if (active && payload && payload.length) {
    const data = payload[0]
    return (
      <div className="bg-popover text-popover-foreground border border-border rounded-lg shadow-xl p-3 text-sm z-50 relative">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: data.payload.color }} />
          <span className="font-semibold">{data.name}</span>
        </div>
        <div className="text-lg font-bold">{data.value.toLocaleString()} tickets</div>
        <div className="text-xs text-muted-foreground">{data.payload.pct?.toFixed(1)}%</div>
      </div>
    )
  }
  return null
}

/**
 * Brand Filter Dropdown (reused pattern from AggregatePnLView)
 */
function BrandFilter({ brands, selectedBrands, onSelectionChange }) {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef(null)

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const allSelected = selectedBrands.length === brands.length

  const toggleBrand = (clinicId) => {
    if (selectedBrands.includes(clinicId)) {
      if (selectedBrands.length > 1) {
        onSelectionChange(selectedBrands.filter(id => id !== clinicId))
      }
    } else {
      onSelectionChange([...selectedBrands, clinicId])
    }
  }

  const selectAll = () => {
    onSelectionChange(brands.map(b => b.clinicId))
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className="h-8 gap-2"
      >
        <Filter className="h-4 w-4" />
        <span className="hidden sm:inline">
          {allSelected ? 'All Brands' : `${selectedBrands.length} Brand${selectedBrands.length !== 1 ? 's' : ''}`}
        </span>
        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </Button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-popover border border-border rounded-lg shadow-lg z-50">
          <div className="p-2">
            <div className="text-xs font-medium text-muted-foreground px-2 py-1 mb-1">
              Filter Brands
            </div>
            <button
              onClick={selectAll}
              className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-muted text-left"
            >
              <div className={`w-4 h-4 rounded border flex items-center justify-center ${allSelected ? 'bg-primary border-primary' : 'border-muted-foreground/30'}`}>
                {allSelected && <Check className="h-3 w-3 text-primary-foreground" />}
              </div>
              <span className="font-medium">Select All</span>
            </button>
            <div className="border-t border-border my-1" />
            {brands.map((brand) => {
              const isSelected = selectedBrands.includes(brand.clinicId)
              const isLastSelected = isSelected && selectedBrands.length === 1
              return (
                <button
                  key={brand.clinicId}
                  onClick={() => toggleBrand(brand.clinicId)}
                  disabled={isLastSelected}
                  className={`w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-muted text-left ${isLastSelected ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className={`w-4 h-4 rounded border flex items-center justify-center ${isSelected ? 'bg-primary border-primary' : 'border-muted-foreground/30'}`}>
                    {isSelected && <Check className="h-3 w-3 text-primary-foreground" />}
                  </div>
                  <span
                    className="w-3 h-3 rounded-full shrink-0"
                    style={{ backgroundColor: brand.color || getBrandColor(brand.clinicId) }}
                  />
                  <span>{brand.displayName}</span>
                </button>
              )
            })}
            {selectedBrands.length === 1 && (
              <p className="text-xs text-muted-foreground px-2 py-1 mt-1">
                At least one brand must be selected
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default function AggregateZendeskView({ data, weeklyData, loading, error, dateRange }) {
  const [selectedBrands, setSelectedBrands] = useState([])
  // P90s for a narrowed brand selection, from the server. A P90 for two brands
  // cannot be rebuilt from each brand's own P90 — averaging them is what made
  // a two-day week read 223h when its tickets said 68h.
  const [subsetP90, setSubsetP90] = useState(null)

  // Initialize selected brands when data loads
  useEffect(() => {
    if (data?.brand_breakdown && selectedBrands.length === 0) {
      setSelectedBrands(data.brand_breakdown.map(b => b.clinicId))
    }
  }, [data?.brand_breakdown])

  // All available brands for filter
  const allBrands = useMemo(() => {
    return data?.brand_breakdown || []
  }, [data?.brand_breakdown])

  const isSubset = selectedBrands.length > 0 && selectedBrands.length < allBrands.length
  const startDate = dateRange?.from ? format(dateRange.from, 'yyyy-MM-dd') : null
  const endDate = dateRange?.to ? format(dateRange.to, 'yyyy-MM-dd') : null
  useEffect(() => {
    if (!isSubset || !startDate || !endDate) { setSubsetP90(null); return }
    let cancelled = false
    setSubsetP90(null)
    zendeskAPI.getResponseTimes({ clinics: selectedBrands.join(','), startDate, endDate, weeks: 12 })
      .then(res => { if (!cancelled) setSubsetP90(res.data) })
      .catch(() => { if (!cancelled) setSubsetP90(null) })
    return () => { cancelled = true }
  }, [isSubset, selectedBrands, startDate, endDate])

  // Filter data by selected brands
  const filteredData = useMemo(() => {
    if (!data || selectedBrands.length === 0) return data
    if (selectedBrands.length === allBrands.length) return data

    const filteredBreakdown = data.brand_breakdown.filter(b => selectedBrands.includes(b.clinicId))

    const summary = filteredBreakdown.reduce((acc, b) => ({
      tickets_created: acc.tickets_created + b.tickets_created,
      tickets_solved: acc.tickets_solved + b.tickets_solved,
      csat_good: acc.csat_good + b.csat_good,
      csat_bad: acc.csat_bad + b.csat_bad,
      csat_offered: acc.csat_offered + b.csat_offered,
    }), { tickets_created: 0, tickets_solved: 0, csat_good: 0, csat_bad: 0, csat_offered: 0 })

    summary.csat_score = summary.csat_offered > 0 ? summary.csat_good / summary.csat_offered : null

    const allReplyTimes = filteredBreakdown.filter(b => b.avg_first_reply_minutes != null).map(b => b.avg_first_reply_minutes)
    const allSolveTimes = filteredBreakdown.filter(b => b.avg_solve_time_hours != null).map(b => b.avg_solve_time_hours)
    summary.avg_first_reply_minutes = allReplyTimes.length > 0
      ? allReplyTimes.reduce((a, b) => a + b, 0) / allReplyTimes.length : null
    summary.avg_solve_time_hours = allSolveTimes.length > 0
      ? allSolveTimes.reduce((a, b) => a + b, 0) / allSolveTimes.length : null

    // Null ('--') until the server answers, rather than a wrong number.
    summary.p90_first_reply_minutes = subsetP90?.summary?.p90_first_reply_minutes ?? null
    summary.p90_solve_time_hours = subsetP90?.summary?.p90_solve_time_hours ?? null

    // Filter daily by_brand
    const daily = data.daily.map(day => {
      const filteredByBrand = {}
      let created = 0
      let solved = 0
      Object.entries(day.by_brand || {}).forEach(([brandId, brandData]) => {
        if (selectedBrands.includes(brandId)) {
          filteredByBrand[brandId] = brandData
          created += brandData.tickets_created || 0
          solved += brandData.tickets_solved || 0
        }
      })
      return { ...day, tickets_created: created, tickets_solved: solved, by_brand: filteredByBrand }
    })

    // Filter CSAT comments and dsats by brand
    const selectedBrandNames = filteredBreakdown.map(b => b.brandName)
    const csat = {
      ...data.csat,
      recent_comments: (data.csat?.recent_comments || []).filter(c =>
        selectedBrandNames.includes(c.brand_name)
      ),
      dsats: (data.csat?.dsats || []).filter(c =>
        selectedBrandNames.includes(c.brand_name)
      ),
    }

    return { ...data, summary, brand_breakdown: filteredBreakdown, daily, csat }
  }, [data, selectedBrands, allBrands, subsetP90])

  // Filter weekly data by selected brands
  const filteredWeekly = useMemo(() => {
    if (!weeklyData || selectedBrands.length === 0) return weeklyData
    if (selectedBrands.length === allBrands.length) return weeklyData

    const weeks = weeklyData.map(w => {
      const filteredByBrand = {}
      let created = 0
      let solved = 0
      Object.entries(w.by_brand || {}).forEach(([brandId, brandData]) => {
        if (selectedBrands.includes(brandId)) {
          filteredByBrand[brandId] = brandData
          created += brandData.tickets_created || 0
          solved += brandData.tickets_solved || 0
        }
      })
      return { ...w, tickets_created: created, tickets_solved: solved, by_brand: filteredByBrand }
    })
    // The selection's own weekly P90s, and the week-on-week changes between them.
    const p90ByWeek = new Map((subsetP90?.weeks || []).map(p => [p.week_start, p]))
    return weeks.map((w, i) => {
      const p90 = p90ByWeek.get(w.week_start) || {}
      const prev = i > 0 ? p90ByWeek.get(weeks[i - 1].week_start) || {} : null
      const change = (now, before) => (now != null && before != null ? now - before : null)
      return {
        ...w,
        p90_first_reply_minutes: p90.p90_first_reply_minutes ?? null,
        p90_solve_time_hours: p90.p90_solve_time_hours ?? null,
        wow_p90_reply_change: prev ? change(p90.p90_first_reply_minutes, prev.p90_first_reply_minutes) : null,
        wow_p90_solve_change: prev ? change(p90.p90_solve_time_hours, prev.p90_solve_time_hours) : null,
      }
    })
  }, [weeklyData, selectedBrands, allBrands, subsetP90])

  // Brand IDs for stacked charts
  const brandIds = useMemo(() => {
    return (filteredData?.brand_breakdown || []).map(b => b.clinicId)
  }, [filteredData])

  // Stacked daily ticket volume data
  const dailyStackedData = useMemo(() => {
    if (!filteredData?.daily) return []
    return filteredData.daily.map(day => {
      const point = { date: day.displayDate, total_created: day.tickets_created, total_solved: day.tickets_solved }
      Object.entries(day.by_brand || {}).forEach(([brandId, brandData]) => {
        point[brandId] = brandData.tickets_created || 0
      })
      return point
    })
  }, [filteredData])

  // Brand ticket distribution for pie chart
  const brandTicketData = useMemo(() => {
    if (!filteredData?.brand_breakdown) return []
    const total = filteredData.brand_breakdown.reduce((sum, b) => sum + b.tickets_created, 0) || 1
    return filteredData.brand_breakdown.map(b => ({
      name: b.displayName,
      value: b.tickets_created,
      color: b.color || getBrandColor(b.clinicId),
      // `pct`, NOT `percent`. Recharts spreads each datum into the label
      // renderer's props, so a `percent` key here SHADOWS the 0-1 fraction
      // recharts computes — and renderPieLabel multiplies by 100, turning 45%
      // into 4520%. The name is load-bearing.
      pct: (b.tickets_created / total) * 100,
    }))
  }, [filteredData])

  // Channel pie data
  const channelPieData = useMemo(() => {
    return (filteredData?.by_channel || []).map(ch => ({
      name: ch.name,
      value: ch.count,
      color: ch.color,
      // See brandTicketData: `percent` would shadow recharts' own fraction.
      pct: ch.percent,
    }))
  }, [filteredData])

  // Weekly volume data with stacking
  const weeklyVolumeData = useMemo(() => {
    if (!filteredWeekly) return []
    return filteredWeekly.map(w => {
      const point = { label: w.label, created: w.tickets_created, solved: w.tickets_solved }
      Object.entries(w.by_brand || {}).forEach(([brandId, brandData]) => {
        point[`${brandId}_created`] = brandData.tickets_created || 0
      })
      return point
    })
  }, [filteredWeekly])

  // Weekly CSAT data
  const weeklyCSATData = useMemo(() => {
    if (!filteredWeekly) return []
    return filteredWeekly.map(w => ({
      label: w.label,
      csat: w.csat_score != null ? Math.round(w.csat_score * 100) : null,
      good: w.csat_good,
      bad: w.csat_bad,
    }))
  }, [filteredWeekly])

  // Weekly P90 data
  const weeklyP90ReplyData = useMemo(() => {
    if (!filteredWeekly) return []
    return filteredWeekly.map(w => ({
      label: w.label,
      p90_minutes: w.p90_first_reply_minutes != null ? Math.round(w.p90_first_reply_minutes) : null,
      change: w.wow_p90_reply_change != null ? Math.round(w.wow_p90_reply_change) : null,
    }))
  }, [filteredWeekly])

  const weeklyP90SolveData = useMemo(() => {
    if (!filteredWeekly) return []
    return filteredWeekly.map(w => ({
      label: w.label,
      p90_hours: w.p90_solve_time_hours != null ? parseFloat(w.p90_solve_time_hours.toFixed(1)) : null,
      change: w.wow_p90_solve_change != null ? parseFloat(w.wow_p90_solve_change.toFixed(1)) : null,
    }))
  }, [filteredWeekly])

  // CSAT feedback split — DSATs: show all (deduped), good: only with content
  const csatFeedback = useMemo(() => {
    const allDsats = filteredData?.csat?.dsats || (filteredData?.csat?.recent_comments || []).filter(c => c.score === 'bad')
    const goodComments = (filteredData?.csat?.recent_comments || []).filter(c =>
      c.score === 'good' &&
      ((c.reason && c.reason !== 'No reason provided') || (c.comment && c.comment.trim() !== ''))
    )
    return { bad: allDsats, good: goodComments }
  }, [filteredData])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">Loading aggregate support data...</p>
        </div>
      </div>
    )
  }

  if (!filteredData) return null

  const summary = filteredData.summary || {}

  return (
    <div className="space-y-6">
      {/* Brand Filter */}
      {allBrands.length > 1 && (
        <div className="flex justify-end">
          <BrandFilter
            brands={allBrands}
            selectedBrands={selectedBrands}
            onSelectionChange={setSelectedBrands}
          />
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Tickets Created</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatNumber(summary.tickets_created || 0)}</div>
            <p className="text-xs text-muted-foreground">
              {selectedBrands.length} brand{selectedBrands.length !== 1 ? 's' : ''}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Tickets Solved</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatNumber(summary.tickets_solved || 0)}</div>
            <p className="text-xs text-muted-foreground">
              {summary.tickets_created > 0
                ? `${((summary.tickets_solved / summary.tickets_created) * 100).toFixed(0)}% solve rate`
                : '--'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">CSAT Score</CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${
              summary.csat_score != null
                ? summary.csat_score >= 0.8 ? 'text-green-600 dark:text-green-400'
                  : summary.csat_score >= 0.6 ? 'text-yellow-600 dark:text-yellow-400'
                  : 'text-red-600 dark:text-red-400'
                : ''
            }`}>
              {formatCSAT(summary.csat_score)}
            </div>
            <p className="text-xs text-muted-foreground">
              {summary.csat_good} good / {summary.csat_offered} offered
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">P90 First Reply</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatMinutes(summary.p90_first_reply_minutes)}</div>
            <p className="text-xs text-muted-foreground">90th percentile response</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">P90 Solve Time</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatHours(summary.p90_solve_time_hours)}</div>
            <p className="text-xs text-muted-foreground">90th percentile resolution</p>
          </CardContent>
        </Card>
      </div>

      {/* Brand Breakdown Table */}
      {filteredData.brand_breakdown && filteredData.brand_breakdown.length > 1 && (
        <Card>
          <CardHeader>
            <CardTitle>Brand Breakdown</CardTitle>
            <CardDescription>Support metrics per brand</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-2 font-medium">Brand</th>
                    <th className="text-right py-3 px-2 font-medium">Created</th>
                    <th className="text-right py-3 px-2 font-medium">Solved</th>
                    <th className="text-right py-3 px-2 font-medium">CSAT</th>
                    <th className="text-right py-3 px-2 font-medium">P90 Reply</th>
                    <th className="text-right py-3 px-2 font-medium">P90 Solve</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.brand_breakdown.map(brand => (
                    <tr key={brand.clinicId} className="border-b border-muted/50 hover:bg-muted/50">
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded-full shrink-0"
                            style={{ backgroundColor: brand.color || getBrandColor(brand.clinicId) }} />
                          <span className="font-medium">{brand.displayName}</span>
                        </div>
                      </td>
                      <td className="text-right py-3 px-2">{formatNumber(brand.tickets_created)}</td>
                      <td className="text-right py-3 px-2">{formatNumber(brand.tickets_solved)}</td>
                      <td className={`text-right py-3 px-2 font-medium ${
                        brand.csat_score != null
                          ? brand.csat_score >= 0.8 ? 'text-green-600 dark:text-green-400'
                            : brand.csat_score >= 0.6 ? 'text-yellow-600 dark:text-yellow-400'
                            : 'text-red-600 dark:text-red-400'
                          : ''
                      }`}>
                        {formatCSAT(brand.csat_score)}
                      </td>
                      <td className="text-right py-3 px-2">{formatMinutes(brand.p90_first_reply_minutes)}</td>
                      <td className="text-right py-3 px-2">{formatHours(brand.p90_solve_time_hours)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-muted/30 font-medium">
                    <td className="py-3 px-2">Total</td>
                    <td className="text-right py-3 px-2">{formatNumber(summary.tickets_created || 0)}</td>
                    <td className="text-right py-3 px-2">{formatNumber(summary.tickets_solved || 0)}</td>
                    <td className={`text-right py-3 px-2 ${
                      summary.csat_score != null
                        ? summary.csat_score >= 0.8 ? 'text-green-600 dark:text-green-400'
                          : summary.csat_score >= 0.6 ? 'text-yellow-600 dark:text-yellow-400'
                          : 'text-red-600 dark:text-red-400'
                        : ''
                    }`}>
                      {formatCSAT(summary.csat_score)}
                    </td>
                    <td className="text-right py-3 px-2">{formatMinutes(summary.p90_first_reply_minutes)}</td>
                    <td className="text-right py-3 px-2">{formatHours(summary.p90_solve_time_hours)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Ticket Volume - Stacked by Brand */}
      {dailyStackedData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Daily Ticket Volume by Brand</CardTitle>
            <CardDescription>Tickets created per day, stacked by brand</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dailyStackedData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    interval={Math.floor(dailyStackedData.length / 8)}
                  />
                  <YAxis tick={{ fontSize: 12 }} tickLine={false} />
                  <Tooltip content={<ChartTooltip />} />
                  <Legend />
                  {brandIds.map((brandId, index) => {
                    const brand = filteredData?.brand_breakdown?.find(b => b.clinicId === brandId)
                    return (
                      <Bar
                        key={brandId}
                        dataKey={brandId}
                        name={brand?.displayName || brandId}
                        stackId="tickets"
                        fill={brand?.color || getBrandColor(brandId)}
                        radius={index === brandIds.length - 1 ? [2, 2, 0, 0] : [0, 0, 0, 0]}
                      />
                    )
                  })}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tickets by Brand + Channel Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Tickets by Brand Pie */}
        <Card>
          <CardHeader>
            <CardTitle>Tickets by Brand</CardTitle>
            <CardDescription>Distribution of created tickets</CardDescription>
          </CardHeader>
          <CardContent>
            {brandTicketData.length > 0 ? (
              <div className="h-72 relative">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={brandTicketData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={renderPieLabel}
                      innerRadius={55}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {brandTicketData.map((entry, index) => (
                        <Cell key={index} fill={entry.color} stroke="hsl(var(--background))" strokeWidth={2} />
                      ))}
                    </Pie>
                    <Tooltip content={<PieTooltip />} wrapperStyle={{ zIndex: 100 }} />
                    <Legend
                      verticalAlign="bottom"
                      height={36}
                      formatter={(value) => <span className="text-foreground text-sm">{value}</span>}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0" style={{ marginBottom: '36px' }}>
                  <div className="text-center">
                    <div className="text-xs text-muted-foreground uppercase tracking-wider">Total</div>
                    <div className="text-lg font-bold text-foreground">
                      {brandTicketData.reduce((sum, d) => sum + d.value, 0).toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="h-72 flex items-center justify-center text-muted-foreground">No data</div>
            )}
          </CardContent>
        </Card>

        {/* Channel Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle>Tickets by Channel</CardTitle>
            <CardDescription>How tickets are submitted across all brands</CardDescription>
          </CardHeader>
          <CardContent>
            {channelPieData.length > 0 ? (
              <div className="h-72 relative">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={channelPieData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={renderPieLabel}
                      innerRadius={55}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {channelPieData.map((entry, index) => (
                        <Cell key={index} fill={entry.color} stroke="hsl(var(--background))" strokeWidth={2} />
                      ))}
                    </Pie>
                    <Tooltip content={<PieTooltip />} wrapperStyle={{ zIndex: 100 }} />
                    <Legend
                      verticalAlign="bottom"
                      height={36}
                      formatter={(value) => <span className="text-foreground text-sm">{value}</span>}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0" style={{ marginBottom: '36px' }}>
                  <div className="text-center">
                    <div className="text-xs text-muted-foreground uppercase tracking-wider">Total</div>
                    <div className="text-lg font-bold text-foreground">
                      {channelPieData.reduce((sum, d) => sum + d.value, 0).toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="h-72 flex items-center justify-center text-muted-foreground">No channel data</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Week over Week Section */}
      {filteredWeekly && filteredWeekly.length > 1 && (
        <>
          <div className="pt-2">
            <h2 className="text-lg font-semibold text-foreground">Week over Week</h2>
            <p className="text-sm text-muted-foreground">Aggregate trends across the last {filteredWeekly.length} weeks</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Weekly Ticket Volume - stacked by brand */}
            <Card>
              <CardHeader>
                <CardTitle>Ticket Volume by Week</CardTitle>
                <CardDescription>Created tickets per week by brand</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={weeklyVolumeData}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} />
                      <YAxis tick={{ fontSize: 12 }} tickLine={false} />
                      <Tooltip content={<ChartTooltip />} />
                      <Legend />
                      {brandIds.map((brandId, index) => {
                        const brand = filteredData?.brand_breakdown?.find(b => b.clinicId === brandId)
                        return (
                          <Bar
                            key={brandId}
                            dataKey={`${brandId}_created`}
                            name={brand?.displayName || brandId}
                            stackId="weekly"
                            fill={brand?.color || getBrandColor(brandId)}
                            radius={index === brandIds.length - 1 ? [2, 2, 0, 0] : [0, 0, 0, 0]}
                          />
                        )
                      })}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Weekly CSAT */}
            <Card>
              <CardHeader>
                <CardTitle>CSAT by Week</CardTitle>
                <CardDescription>Aggregate customer satisfaction trend</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={weeklyCSATData}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} />
                      <YAxis
                        yAxisId="left"
                        domain={[0, 100]}
                        tick={{ fontSize: 12 }}
                        tickLine={false}
                        tickFormatter={(v) => `${v}%`}
                      />
                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        tick={{ fontSize: 12 }}
                        tickLine={false}
                      />
                      <Tooltip content={<ChartTooltip valueFormatter={(v, name) => {
                        if (name === 'CSAT %') return v != null ? `${v}%` : '--'
                        return v
                      }} />} />
                      <Legend />
                      <Bar yAxisId="right" dataKey="good" name="Good" fill="#22C55E" opacity={0.5} radius={[2, 2, 0, 0]} />
                      <Bar yAxisId="right" dataKey="bad" name="Bad" fill="#EF4444" opacity={0.5} radius={[2, 2, 0, 0]} />
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="csat"
                        name="CSAT %"
                        stroke="#22C55E"
                        strokeWidth={2.5}
                        dot={{ r: 4, fill: '#22C55E' }}
                        connectNulls
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Weekly P90 First Response */}
            <Card>
              <CardHeader>
                <CardTitle>P90 First Response by Week</CardTitle>
                <CardDescription>90th percentile first reply time (minutes)</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={weeklyP90ReplyData}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} />
                      <YAxis tick={{ fontSize: 12 }} tickLine={false} tickFormatter={(v) => formatMinutes(v)} />
                      <Tooltip content={<ChartTooltip valueFormatter={(v) => formatMinutes(v)} />} />
                      <Legend />
                      <Area
                        type="monotone"
                        dataKey="p90_minutes"
                        name="P90 First Reply"
                        stroke="#3B82F6"
                        fill="#3B82F6"
                        fillOpacity={0.1}
                        strokeWidth={2}
                        connectNulls
                      />
                      <Line
                        type="monotone"
                        dataKey="p90_minutes"
                        name="P90 First Reply"
                        stroke="#3B82F6"
                        strokeWidth={2.5}
                        dot={{ r: 4, fill: '#3B82F6' }}
                        connectNulls
                        legendType="none"
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex gap-2 mt-3 flex-wrap">
                  {weeklyP90ReplyData.slice(-4).map((w, i) => (
                    w.change != null && (
                      <div key={i} className="flex items-center gap-1 text-xs">
                        <span className="text-muted-foreground">{w.label}:</span>
                        <span className={w.change < 0 ? 'text-green-500' : w.change > 0 ? 'text-red-500' : 'text-muted-foreground'}>
                          {w.change > 0 ? '+' : ''}{formatMinutes(w.change)}
                        </span>
                      </div>
                    )
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Weekly P90 Solve Time */}
            <Card>
              <CardHeader>
                <CardTitle>P90 Solve Time by Week</CardTitle>
                <CardDescription>90th percentile resolution time (hours)</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={weeklyP90SolveData}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} />
                      <YAxis tick={{ fontSize: 12 }} tickLine={false} tickFormatter={(v) => formatHours(v)} />
                      <Tooltip content={<ChartTooltip valueFormatter={(v) => formatHours(v)} />} />
                      <Legend />
                      <Area
                        type="monotone"
                        dataKey="p90_hours"
                        name="P90 Solve Time"
                        stroke="#F59E0B"
                        fill="#F59E0B"
                        fillOpacity={0.1}
                        strokeWidth={2}
                        connectNulls
                      />
                      <Line
                        type="monotone"
                        dataKey="p90_hours"
                        name="P90 Solve Time"
                        stroke="#F59E0B"
                        strokeWidth={2.5}
                        dot={{ r: 4, fill: '#F59E0B' }}
                        connectNulls
                        legendType="none"
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex gap-2 mt-3 flex-wrap">
                  {weeklyP90SolveData.slice(-4).map((w, i) => (
                    w.change != null && (
                      <div key={i} className="flex items-center gap-1 text-xs">
                        <span className="text-muted-foreground">{w.label}:</span>
                        <span className={w.change < 0 ? 'text-green-500' : w.change > 0 ? 'text-red-500' : 'text-muted-foreground'}>
                          {w.change > 0 ? '+' : ''}{formatHours(w.change)}
                        </span>
                      </div>
                    )
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {/* CSAT Feedback - split by good/bad with brand badges */}
      {(csatFeedback.bad.length > 0 || csatFeedback.good.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* All DSATs */}
          <Card className="border-red-200 dark:border-red-900/50">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Badge variant="destructive">Bad</Badge>
                <CardTitle className="text-base">All Negative Ratings</CardTitle>
              </div>
              <CardDescription>
                {csatFeedback.bad.length} DSAT{csatFeedback.bad.length !== 1 ? 's' : ''} in this period (deduped per ticket)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {csatFeedback.bad.length > 0 ? (
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {csatFeedback.bad.map((c, i) => {
                    const brand = allBrands.find(b => b.brandName === c.brand_name)
                    const hasReason = c.reason && c.reason !== 'No reason provided'
                    const hasComment = c.comment && c.comment.trim() !== ''
                    return (
                      <div key={i} className="border border-red-200 dark:border-red-900/40 bg-red-50/50 dark:bg-red-950/20 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center gap-2">
                            {hasReason && <span className="text-xs font-medium text-red-700 dark:text-red-400">{c.reason}</span>}
                            {brand && (
                              <span className="text-xs px-1.5 py-0.5 rounded-full" style={{
                                backgroundColor: `${brand.color}20`,
                                color: brand.color,
                              }}>
                                {brand.displayName}
                              </span>
                            )}
                          </div>
                          <span className="text-xs text-muted-foreground ml-auto">
                            {c.created_at ? new Date(c.created_at).toLocaleDateString('en-US', {
                              month: 'short', day: 'numeric'
                            }) : ''}
                          </span>
                        </div>
                        {hasComment ? (
                          <p className="text-sm text-foreground">{c.comment}</p>
                        ) : !hasReason ? (
                          <p className="text-sm text-muted-foreground italic">No feedback provided</p>
                        ) : null}
                        <p className="text-xs text-muted-foreground mt-1">Ticket #{c.ticket_id}</p>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground py-4 text-center">No negative ratings in this period</p>
              )}
            </CardContent>
          </Card>

          {/* Good Feedback */}
          <Card className="border-green-200 dark:border-green-900/50">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Badge variant="default">Good</Badge>
                <CardTitle className="text-base">Positive Feedback</CardTitle>
              </div>
              <CardDescription>
                {csatFeedback.good.length} rated good with feedback
              </CardDescription>
            </CardHeader>
            <CardContent>
              {csatFeedback.good.length > 0 ? (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {csatFeedback.good.map((c, i) => {
                    const brand = allBrands.find(b => b.brandName === c.brand_name)
                    const hasReason = c.reason && c.reason !== 'No reason provided'
                    return (
                      <div key={i} className="border border-green-200 dark:border-green-900/40 bg-green-50/50 dark:bg-green-950/20 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center gap-2">
                            {hasReason && <span className="text-xs font-medium text-green-700 dark:text-green-400">{c.reason}</span>}
                            {brand && (
                              <span className="text-xs px-1.5 py-0.5 rounded-full" style={{
                                backgroundColor: `${brand.color}20`,
                                color: brand.color,
                              }}>
                                {brand.displayName}
                              </span>
                            )}
                          </div>
                          <span className="text-xs text-muted-foreground ml-auto">
                            {c.created_at ? new Date(c.created_at).toLocaleDateString('en-US', {
                              month: 'short', day: 'numeric'
                            }) : ''}
                          </span>
                        </div>
                        {c.comment && (
                          <p className="text-sm text-foreground">{c.comment}</p>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">Ticket #{c.ticket_id}</p>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground py-4 text-center">No positive feedback in this period</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}
